// ============================================================
// ChannelView — /communications/channels/:id and /communications/dms/:id.
//
// PHASE_2M D2 "Quantum Comms Cockpit": the active-thread pane. Message
// BUBBLES (subtle gradient, sender initial badge, timestamp) with a hover
// action row — Reply (quotes into the composer), Copy (real clipboard),
// Create task / Mark blocker (REAL sq_team_tasks rows tied to THIS message,
// audited), Delete own message (BACKEND_DEFERRED — no delete route exists;
// rendered disabled, never faked). Composer pinned at the bottom of the
// thread card: SVG attach control (INTEGRATION_DEFERRED — message
// attachments have no storage linking yet) + a refined send button.
// Right rail = the CONTEXT PANEL: participants (member-list route pending —
// exact, not broad), linked portal (deferred), shared media (honest none),
// receipts note, call actions (real route), and REAL tasks created from
// this channel's messages.
//
// Chat #2 conversion: presentation lives in comms-surface.css. The only inline
// styles are layout and the per-sender avatar hue (dynamic, passed as the
// --sqv-h / --sqv-h2 custom properties). The sqv2-msg / sqv2-msg-actions
// classes + their hover/focus reveal (CommsFrame <style> seam) are preserved.
// ============================================================

import { useMemo, useState } from 'react'
import type { CSSProperties } from 'react'
import CommsFrame, { ChannelRail, StateBlock } from './CommsShell'
import { StatusBadge } from '../operations'
import { FileIcon, CopyIcon, TaskIcon, WarnIcon, TrashIcon, MeetingIcon, OpenIcon } from '../shell/icons'
import { COMMS_ROOT, TEAM_ROOT, type Channel, type Message, type TeamTask } from './commsData'

export interface ChannelViewProps {
  channel?: Channel
  channels: Channel[]
  dms: Channel[]
  messages: Message[]
  loading: boolean
  error: string | null
  authRequired: boolean
  /** Channel-list (rail) load state — distinct from the message load state. */
  railLoading?: boolean
  railError?: string | null
  railAuthRequired?: boolean
  isDm?: boolean
  /** REAL tasks whose source_channel_id is this channel (context panel). */
  channelTasks?: TeamTask[]
  onSend?: (body: string) => void | Promise<unknown>
  onCreateTask?: () => void | Promise<unknown>
  onFlagBlocker?: () => void | Promise<unknown>
  /** REAL per-message actions — sq_team_tasks rows with source_message_id. */
  onCreateTaskFromMessage?: (msg: Message) => void | Promise<unknown>
  onFlagBlockerFromMessage?: (msg: Message) => void | Promise<unknown>
  onOpenTask?: (id: string) => void
  onStartConferenceCall?: () => void
  onOpenChannel?: (id: string) => void
  onOpenDm?: (id: string) => void
  onOpenTeam?: () => void
  onOpenConferenceCalls?: () => void
  onOpenTasks?: () => void
  onHome?: () => void
  onBackCommandCenter?: () => void
  onNavTop?: (to: string) => void
}

function initialBadge(senderId: string) {
  const initial = (senderId || '?').slice(0, 1).toUpperCase()
  // deterministic hue from the sender id — consistent per person, no data invented
  const hue = (senderId || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % 360
  return (
    <span className="sqv-comms-avatar" style={{ ['--sqv-h']: hue, ['--sqv-h2']: (hue + 40) % 360 } as CSSProperties}>
      {initial}
    </span>
  )
}

function MessageBubble({ msg, onReply, onCreateTask, onFlagBlocker }: {
  msg: Message
  onReply: (msg: Message) => void
  onCreateTask?: (msg: Message) => void
  onFlagBlocker?: (msg: Message) => void
}) {
  return (
    <div className="sqv2-msg" style={{ display: 'flex', gap: 9, alignItems: 'flex-start' }}>
      {initialBadge(msg.sender_id)}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="sqv-comms-bubble">
          <div className="sqv-comms-bubble-meta">
            {msg.sender_id.slice(0, 8)} · {msg.created_at.slice(0, 16).replace('T', ' ')}
          </div>
          <div className="sqv-comms-bubble-body">{msg.body}</div>
        </div>
        <div className="sqv2-msg-actions" style={{ display: 'flex', gap: 6, marginTop: 4, minHeight: 22 }}>
          <button type="button" className="sqv-comms-msgbtn" data-tone="reply" onClick={() => onReply(msg)}>REPLY</button>
          <button type="button" className="sqv-comms-msgbtn" data-tone="copy" onClick={() => { void navigator.clipboard?.writeText(msg.body) }}><CopyIcon size={10} /> COPY</button>
          {onCreateTask && <button type="button" className="sqv-comms-msgbtn" data-tone="task" title="Creates a REAL sq_team_tasks row tied to this message (audited)." onClick={() => onCreateTask(msg)}><TaskIcon size={10} /> TASK</button>}
          {onFlagBlocker && <button type="button" className="sqv-comms-msgbtn" data-tone="blocker" title="Creates a REAL blocked task tied to this message (audited)." onClick={() => onFlagBlocker(msg)}><WarnIcon size={10} /> BLOCKER</button>}
          <button type="button" disabled className="sqv-comms-msgbtn" data-tone="delete" title="Deleting a message has no backend route yet (BACKEND_DEFERRED) — soft-delete + audit is the planned shape. Never faked."><TrashIcon size={10} /> DELETE</button>
        </div>
      </div>
    </div>
  )
}

export default function ChannelView(props: ChannelViewProps) {
  const { channel, channels, dms, messages, loading, error, authRequired, railLoading, railError, railAuthRequired, isDm, channelTasks = [], onSend, onCreateTask, onFlagBlocker, onCreateTaskFromMessage, onFlagBlockerFromMessage, onOpenTask, onStartConferenceCall, onOpenChannel, onOpenDm, onOpenTeam, onOpenConferenceCalls, onOpenTasks, onHome, onBackCommandCenter, onNavTop } = props
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const send = async () => {
    if (!draft.trim() || !onSend) return
    setSending(true)
    try { await onSend(draft.trim()); setDraft('') } finally { setSending(false) }
  }
  const reply = (msg: Message) => {
    const quote = msg.body.length > 80 ? `${msg.body.slice(0, 80)}…` : msg.body
    setDraft((d) => `> ${quote}\n${d}`)
  }

  const label = channel?.name || (isDm ? 'Direct message' : 'Channel')
  const openTasks = useMemo(() => channelTasks.filter((t) => t.status !== 'done'), [channelTasks])

  const center = (
    <div className="sqv-surface-comms" style={{ gap: 12 }}>
      <div className="sqv-comms-card" style={{ minHeight: 380, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <h3 className="sqv-comms-title sqv-comms-title-flush">{isDm ? '@' : '#'} {label}</h3>
          {channel?.description && <span className="sqv-comms-c-3" style={{ fontSize: 11.5 }}>{channel.description}</span>}
          <span className="sqv-comms-c-4 sqv-comms-mono" style={{ marginLeft: 'auto', fontSize: 9.5 }}>{channel?.visibility || ''}</span>
        </div>
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {authRequired || loading || error || messages.length === 0 ? (
            <StateBlock loading={loading} error={error} authRequired={authRequired} empty={messages.length === 0} emptyLabel="No messages yet — say the first thing. (No seeded chat history.)" />
          ) : (
            messages.map((msg) => (
              <MessageBubble
                key={msg.id}
                msg={msg}
                onReply={reply}
                onCreateTask={onCreateTaskFromMessage}
                onFlagBlocker={onFlagBlockerFromMessage}
              />
            ))
          )}
        </div>
        {/* composer — pinned at the bottom of the thread card */}
        <div style={{ display: 'flex', gap: 8, marginTop: 14, alignItems: 'center' }}>
          <button
            type="button"
            disabled
            title="Message attachments are INTEGRATION_DEFERRED — messages have no storage linking yet. The files spine exists; the message-attachment bridge does not. Never faked."
            className="sqv-comms-attach"
          >
            <FileIcon size={14} />
          </button>
          <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') send() }} placeholder={authRequired ? 'Sign in to post…' : `Message ${isDm ? '' : '#'}${label}`} aria-label="Message composer" disabled={authRequired || !channel} className="sqv-comms-input" style={{ flex: 1 }} />
          <button
            type="button"
            onClick={send}
            disabled={authRequired || sending || !channel || !draft.trim()}
            aria-label="Send message"
            className="sqv-comms-send"
            data-active={draft.trim() ? 'true' : 'false'}
            style={{ opacity: authRequired || !channel ? 0.5 : 1 }}
          >
            {sending ? <span className="sqv-comms-mono" style={{ fontSize: 8 }}>…</span> : <OpenIcon size={14} />}
          </button>
        </div>
        <p className="sqv-comms-hint sqv-comms-c-4 sqv-comms-mono" style={{ fontSize: 10.5 }}>Messages post to the real channel (sender is you, server-side). Realtime is REALTIME_DEFERRED — new messages load on send/refresh.</p>
      </div>
    </div>
  )

  const rightRail = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div className="sqv-comms-card">
        <h3 className="sqv-comms-title">Participants</h3>
        <p className="sqv-comms-m0 sqv-comms-c-3" style={{ fontSize: 11, lineHeight: 1.6 }}>
          Membership is real (sq_channel_members) and enforced by RLS; a member-LIST route is pending — this panel shows it the moment that route exists. Not fabricated in the meantime.
        </p>
      </div>
      <div className="sqv-comms-card">
        <h3 className="sqv-comms-title">Call actions</h3>
        <button type="button" onClick={() => onStartConferenceCall?.()} className="sqv-comms-btn" style={{ width: '100%', textAlign: 'left', display: 'inline-flex', alignItems: 'center', gap: 7 }}>
          <MeetingIcon size={12} still /> Start a conference call
        </button>
        <p className="sqv-comms-hint sqv-comms-c-4 sqv-comms-mono" style={{ fontSize: 10 }}>Opens the Daily.co surface — create/join/cancel are fully wired (provider-gated).</p>
      </div>
      <div className="sqv-comms-card">
        <h3 className="sqv-comms-title">Tasks from this channel</h3>
        {openTasks.length === 0 ? (
          <p className="sqv-comms-mb10 sqv-comms-c-3" style={{ fontSize: 11, lineHeight: 1.6 }}>None yet — hover a message and press TASK, or use the buttons below. Real rows only.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
            {openTasks.slice(0, 6).map((t) => (
              <button key={t.id} type="button" onClick={() => onOpenTask?.(t.id)} className="sqv-comms-tilebtn">
                <span className="sqv-comms-miniflag" data-blocked={t.status === 'blocked' ? 'true' : 'false'}>{t.status.replace('_', ' ').toUpperCase()}</span>
                {t.title}
              </button>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button type="button" onClick={() => onCreateTask?.()} disabled={authRequired || !channel} className="sqv-comms-btn" style={{ textAlign: 'left' }}>Create task from this channel</button>
          <button type="button" onClick={() => onFlagBlocker?.()} disabled={authRequired || !channel} className="sqv-comms-btn sqv-comms-btn-danger" style={{ textAlign: 'left' }}>Flag a blocker</button>
        </div>
      </div>
      <div className="sqv-comms-card">
        <h3 className="sqv-comms-title">Context</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          <div className="sqv-comms-ctxrow" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="sqv-comms-c-2" style={{ fontSize: 12, flex: 1 }}>Linked project portal</span>
            <StatusBadge status="INTEGRATION_DEFERRED" size="sm" />
          </div>
          <div className="sqv-comms-ctxrow" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="sqv-comms-c-2" style={{ fontSize: 12, flex: 1 }}>Shared media / files</span>
            <StatusBadge status="INTEGRATION_DEFERRED" size="sm" />
          </div>
          <div className="sqv-comms-ctxrow" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="sqv-comms-c-2" style={{ fontSize: 12, flex: 1 }}>Pin message</span>
            <StatusBadge status="BACKEND_DEFERRED" size="sm" />
          </div>
        </div>
        <p className="sqv-comms-hint9 sqv-comms-c-4 sqv-comms-mono" style={{ fontSize: 10 }}>
          Receipts: every send / task / blocker here is already OLIVIUM-audited automatically — a per-channel receipt feed is planned.
        </p>
      </div>
    </div>
  )

  return (
    <CommsFrame
      activeTop="communications"
      surface={isDm ? 'dm' : 'channel'}
      breadcrumb={isDm ? ['COMMAND CENTER', 'COMMUNICATIONS', 'DM', label.toUpperCase()] : ['COMMAND CENTER', 'COMMUNICATIONS', label.toUpperCase()]}
      title={(isDm ? '@ ' : '# ') + label}
      realtimeBadge
      onHome={onHome}
      onBackCommandCenter={onBackCommandCenter}
      onNavTop={onNavTop}
      leftRail={<ChannelRail channels={channels} dms={dms} activeId={channel?.id} loading={railLoading} error={railError} authRequired={railAuthRequired} onOpenChannel={onOpenChannel} onOpenDm={onOpenDm} onOpenTeam={onOpenTeam} onOpenConferenceCalls={onOpenConferenceCalls} onOpenTasks={onOpenTasks} onOpenHome={() => onNavTop?.(COMMS_ROOT)} activeNav={isDm ? 'dms' : 'channels'} />}
      center={center}
      rightRail={rightRail}
    />
  )
}
